import React, { useContext, useEffect, useRef } from 'react';
import { AnimatePresence, motion, PanInfo, useDragControls } from 'framer-motion';
import { VaultThemeContext } from '../useVaultTheme';
import '../vault.css';

interface VaultSheetProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
  zIndex?: number;
  label?: string;
  // Tall content (inbox, settings) scrolls inside the sheet instead of growing it.
  tall?: boolean;
}

// Shared sheet: slides up from the bottom on phones (drag it down to close),
// a centred card on wider screens. Colours come from the surrounding `.vault`
// theme; outside the vault it falls back to the dark glass.
const VaultSheet: React.FC<VaultSheetProps> = ({
  open,
  onClose,
  children,
  maxWidth = 'sm:max-w-md',
  zIndex = 70,
  label,
  tall = false,
}) => {
  const theme = useContext(VaultThemeContext);
  const cardRef = useRef<HTMLDivElement>(null);
  // Drag starts from the handle only, so lists inside the sheet still scroll.
  const drag = useDragControls();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Move focus into the sheet so keyboard and screen-reader users land in it.
    const t = setTimeout(() => {
      const card = cardRef.current;
      if (card && !card.contains(document.activeElement)) {
        (card.querySelector<HTMLElement>('[autofocus], input, button') ?? card).focus({ preventScroll: true });
      }
    }, 60);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 110 || info.velocity.y > 600) onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="vault fixed inset-0 flex items-end justify-center sm:items-center sm:p-4"
          data-vault-theme={theme}
          style={{ zIndex, background: 'var(--v-scrim)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            ref={cardRef}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            tabIndex={-1}
            className={`v-sheet relative flex w-full ${maxWidth} flex-col rounded-t-[16px] px-5 pb-[calc(20px+env(safe-area-inset-bottom))] pt-3 outline-none sm:rounded-[8px] sm:pb-6 sm:pt-5 ${
              tall ? 'max-h-[88dvh]' : 'max-h-[92dvh]'
            }`}
            initial={{ y: '100%', opacity: 0.6 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.6 }}
            transition={{ type: 'spring', damping: 30, stiffness: 340, mass: 0.9 }}
            drag="y"
            dragControls={drag}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.04, bottom: 0.6 }}
            onDragEnd={onDragEnd}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="-mx-5 -mt-3 mb-1 flex cursor-grab touch-none justify-center pb-2 pt-3 sm:hidden"
              aria-hidden="true"
              onPointerDown={e => drag.start(e)}
            >
              <span className="v-handle" />
            </div>
            {/* Tall content scrolls edge to edge, so the scrollbar runs along
                the sheet's side instead of over the content. */}
            <div className={tall ? 'v-sheet-scroll -mx-5 min-h-0 flex-1 overflow-y-auto overscroll-contain px-5' : undefined}>
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VaultSheet;
